import { useMemo } from "react";
const FORM_LENGTH = 5;
const isLeagueMatch = (match) => {
    return "homeTeam" in match;
};
const extractTeamsAndScore = (match) => {
    if (isLeagueMatch(match)) {
        const result = match.leagueMatchResult;
        return {
            teamA: match.homeTeam,
            teamB: match.awayTeam,
            scoreA: result?.homeTeamScore ?? null,
            scoreB: result?.awayTeamScore ?? null,
            kickOff: match.kickOff,
        };
    }
    if (!match.teamA?.id || !match.teamB?.id)
        return null;
    const result = match.intraSquadTournamentMatchResult;
    return {
        teamA: match.teamA,
        teamB: match.teamB,
        scoreA: result?.teamAScore ?? null,
        scoreB: result?.teamBScore ?? null,
        kickOff: match.kickOff,
    };
};
export const useLeagueTable = (matches, teams) => {
    return useMemo(() => {
        const table = new Map();
        const teamMatchResults = new Map();
        const ensureTeam = (team) => {
            if (!table.has(team.id)) {
                table.set(team.id, {
                    team,
                    played: 0,
                    wins: 0,
                    draws: 0,
                    losses: 0,
                    goalsFor: 0,
                    goalsAgainst: 0,
                    goalDifference: 0,
                    points: 0,
                    form: [],
                });
                teamMatchResults.set(team.id, []);
            }
            return table.get(team.id);
        };
        // Pre-seed teams if provided
        if (teams) {
            teams.forEach((team) => ensureTeam(team));
        }
        // Initialize all teams and process results in one pass
        matches.forEach((match) => {
            // Only regular matches count toward league standings
            if (isLeagueMatch(match) && match.matchType !== "regular")
                return;
            const extracted = extractTeamsAndScore(match);
            if (!extracted)
                return;
            const { teamA, teamB, scoreA, scoreB, kickOff } = extracted;
            ensureTeam(teamA);
            ensureTeam(teamB);
            if (scoreA === null || scoreB === null)
                return;
            const standingA = table.get(teamA.id);
            const standingB = table.get(teamB.id);
            standingA.played += 1;
            standingA.goalsFor += scoreA;
            standingA.goalsAgainst += scoreB;
            standingA.goalDifference = standingA.goalsFor - standingA.goalsAgainst;
            standingB.played += 1;
            standingB.goalsFor += scoreB;
            standingB.goalsAgainst += scoreA;
            standingB.goalDifference = standingB.goalsFor - standingB.goalsAgainst;
            if (scoreA > scoreB) {
                standingA.wins += 1;
                standingA.points += 3;
                standingB.losses += 1;
                teamMatchResults.get(teamA.id).push({ kickOff, result: "W" });
                teamMatchResults.get(teamB.id).push({ kickOff, result: "L" });
            }
            else if (scoreB > scoreA) {
                standingB.wins += 1;
                standingB.points += 3;
                standingA.losses += 1;
                teamMatchResults.get(teamA.id).push({ kickOff, result: "L" });
                teamMatchResults.get(teamB.id).push({ kickOff, result: "W" });
            }
            else {
                standingA.draws += 1;
                standingA.points += 1;
                standingB.draws += 1;
                standingB.points += 1;
                teamMatchResults.get(teamA.id).push({ kickOff, result: "D" });
                teamMatchResults.get(teamB.id).push({ kickOff, result: "D" });
            }
        });
        // Compute form (last N results, ordered oldest first for display)
        for (const [teamId, results] of teamMatchResults.entries()) {
            const standing = table.get(teamId);
            const sorted = results.sort((a, b) => a.kickOff.localeCompare(b.kickOff));
            standing.form = sorted.slice(-FORM_LENGTH).map((r) => r.result);
        }
        return Array.from(table.values()).sort((a, b) => b.points - a.points ||
            b.goalDifference - a.goalDifference ||
            b.goalsFor - a.goalsFor ||
            a.team.name.localeCompare(b.team.name));
    }, [matches, teams]);
};
